import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
} from 'react';
import {
  AdDetails,
  GetAdsResponse,
  GetPopularRequestsResponse,
} from '@/api/types/ads.types';
import { GetRootCategoriesResponse } from '@/api/types/categories.types';

interface ServerDataContextProps {
  initialAdDetails: AdDetails | null;
  initialPremiumAds: GetAdsResponse | null;
  popularRequests: GetPopularRequestsResponse | null;
  rootCategories: GetRootCategoriesResponse | null;
}

const ServerDataContext = createContext<ServerDataContextProps | undefined>(
  undefined
);

export const ServerDataProvider = ({
  children,
  adDetails,
  premiumAds,
  popularRequests,
  rootCategories,
}: {
  children: ReactNode;
  adDetails?: AdDetails | null;
  premiumAds?: GetAdsResponse | null;
  popularRequests?: GetPopularRequestsResponse | null;
  rootCategories?: GetRootCategoriesResponse | null;
}) => {
  const [resettableData, setResettableData] = useState({
    initialPremiumAds: premiumAds ?? null,
    popularRequests: popularRequests ?? null,
    rootCategories: rootCategories ?? null,
  });

  // Reset all unused data on the client side
  useEffect(() => {
    setResettableData({
      initialPremiumAds: null,
      popularRequests: null,
      rootCategories: null,
    });
  }, []);

  return (
    <ServerDataContext.Provider
      value={{
        initialAdDetails: adDetails ?? null,
        ...resettableData,
      }}
    >
      {children}
    </ServerDataContext.Provider>
  );
};

export const useServerDataContext = () => {
  const context = useContext(ServerDataContext);
  if (!context) {
    throw new Error(
      'useServerDataContext must be used within ServerDataProvider'
    );
  }
  return context;
};
